import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { connect } from "react-redux";
import moment from "moment";
import { PageHeader, Select, Spin, Empty, Tag, Popconfirm, message } from "antd";
import { ReloadOutlined, PlusOutlined, CheckOutlined, StopOutlined, UndoOutlined, EditOutlined } from "@ant-design/icons";
import {
  ButtonItem,
  SelectItem,
  SelectEmpleadosItem,
  CalendarItem,
  DatePickerItem,
} from "../../../../Components/Items";
import ModalTareaHabitacion, { PRIORIDADES, ESTATUS_TAREA } from "./ModalTareaHabitacion";

const { Option } = Select;

const prioridadDe = (valor) =>
  PRIORIDADES.find((p) => p.valor === valor) || PRIORIDADES[1];

// Color del evento en el calendario. Manda el estatus: una tarea terminada o cancelada ya no compite
// por la atención, aunque fuera urgente.
const colorDeTarea = (tarea) => {
  if (tarea.estatus === "terminada") return ESTATUS_TAREA.terminada.color;
  if (tarea.estatus === "cancelada") return ESTATUS_TAREA.cancelada.color;
  return prioridadDe(tarea.prioridad).color;
};

//Pantalla "Agenda": el trabajo pendiente del hotel en un calendario mensual, con la lista del día que
//se elija. Es el complemento operativo del Mapa de Hotel — el mapa dice quién está en cada cuarto,
//esto dice qué hay que hacerle y a quién le toca.
//
//El calendario es `CalendarItem` (react-big-calendar), el mismo que usa el CRM, para no mantener dos.
const AgendaHotel = ({ empresaId, headersToken }) => {
  const [todosProyectos, setTodosProyectos] = useState([]);
  const [proyectoId, setProyectoId] = useState(undefined);
  const [niveles, setNiveles] = useState([]);
  const [nivelHotelId, setNivelHotelId] = useState(undefined);
  const [responsableEmpleadoId, setResponsableEmpleadoId] = useState(undefined);
  const [estatus, setEstatus] = useState(["pendiente"]);
  const [cargando, setCargando] = useState(false);
  const [datos, setDatos] = useState(null);
  const [diaElegido, setDiaElegido] = useState(moment());
  const [modal, setModal] = useState({ visible: false, tarea: null });

  // El rango que pide el calendario al cambiar de mes. Arranca en el mes en curso.
  const [rango, setRango] = useState({
    desde: moment().startOf("month").format("YYYY-MM-DD"),
    hasta: moment().endOf("month").format("YYYY-MM-DD"),
  });

  useEffect(() => {
    axios
      .post("proyectos/selectProyectos", { empresaId }, { headers: headersToken })
      .then((res) => {
        if (res.data.status === 404) return;
        setTodosProyectos(res.data || []);
      })
      .catch((error) => console.error("Error al cargar los hoteles:", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!proyectoId) {
      setNiveles([]);
      setNivelHotelId(undefined);
      return;
    }
    setNivelHotelId(undefined);
    axios
      .post("hoteleria/selectNivelesHotel", { empresaId, proyectoId }, { headers: headersToken })
      .then((res) => {
        if (res.data.success !== 2) return;
        setNiveles(res.data.niveles || []);
      })
      .catch((error) => console.error("Error al cargar los pisos:", error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, headersToken, proyectoId]);

  const consultar = useCallback(() => {
    if (!proyectoId) {
      setDatos(null);
      return;
    }
    setCargando(true);
    axios
      .post(
        "hoteleria/selectTareasHabitacion",
        {
          empresaId,
          proyectoId,
          nivelHotelId,
          responsableEmpleadoId,
          estatus: estatus.length ? estatus : undefined,
          desde: rango.desde,
          hasta: rango.hasta,
        },
        { headers: headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          setDatos(res.data);
          return;
        }
        setDatos(null);
        message.warning(res.data.message || "No se pudieron cargar las tareas");
      })
      .catch((error) => console.error("Error al cargar las tareas:", error))
      .finally(() => setCargando(false));
  }, [empresaId, headersToken, proyectoId, nivelHotelId, responsableEmpleadoId, estatus, rango]);

  useEffect(() => {
    consultar();
  }, [consultar]);

  const tareas = useMemo(() => datos?.tareas || [], [datos]);
  const habitaciones = useMemo(() => datos?.habitaciones || [], [datos]);

  // Lo que el calendario entiende. `subtitulo` es el campo genérico que CalendarItem pinta arriba del
  // título; en el CRM es el nombre del lead, aquí el número de habitación.
  const eventos = useMemo(
    () =>
      tareas.map((tarea) => ({
        id: tarea._id,
        title: tarea.titulo,
        start: new Date(tarea.fechaInicio),
        end: new Date(tarea.fechaFin || tarea.fechaInicio),
        allDay: tarea.todoElDia !== false,
        completado: tarea.estatus !== "pendiente",
        color: colorDeTarea(tarea),
        descripcion: tarea.descripcion,
        subtitulo: `Hab. ${tarea.habitacionNumero}`,
      })),
    [tareas]
  );

  const claveDia = (fecha) => moment(fecha).format("YYYY-MM-DD");
  const tareasDelDia = useMemo(
    () => tareas.filter((tarea) => claveDia(tarea.fechaInicio) === claveDia(diaElegido)),
    [tareas, diaElegido]
  );

  const manejarRango = (nuevoRango) => {
    // react-big-calendar entrega o un arreglo de días (semana/día) o {start,end} (mes).
    if (Array.isArray(nuevoRango) && nuevoRango.length) {
      setRango({
        desde: moment(nuevoRango[0]).format("YYYY-MM-DD"),
        hasta: moment(nuevoRango[nuevoRango.length - 1]).format("YYYY-MM-DD"),
      });
      return;
    }
    if (nuevoRango && nuevoRango.start) {
      setRango({
        desde: moment(nuevoRango.start).format("YYYY-MM-DD"),
        hasta: moment(nuevoRango.end || nuevoRango.start).format("YYYY-MM-DD"),
      });
    }
  };

  const cambiarEstatus = (tarea, nuevoEstatus) => {
    axios
      .post(
        "hoteleria/cambiarEstatusTareaHabitacion",
        { empresaId, tareaId: tarea._id, estatus: nuevoEstatus },
        { headers: headersToken }
      )
      .then((res) => {
        if (res.data.success !== 2) {
          message.warning(res.data.message || "No se pudo cambiar el estatus");
          return;
        }
        message.success("Tarea actualizada");
        consultar();
      })
      .catch((error) => console.error("Error al cambiar el estatus:", error));
  };

  const eliminar = (tarea) => {
    axios
      .post(
        "hoteleria/eliminarTareaHabitacion",
        { empresaId, tareaId: tarea._id },
        { headers: headersToken }
      )
      .then((res) => {
        if (res.data.success !== 2) {
          message.warning(res.data.error || "No se pudo eliminar la tarea");
          return;
        }
        message.success("Tarea eliminada");
        consultar();
      })
      .catch((error) => console.error("Error al eliminar la tarea:", error));
  };

  const cerrarModal = () => setModal({ visible: false, tarea: null });
  const guardado = () => {
    cerrarModal();
    consultar();
  };

  return (
    <>
      <PageHeader
        title="Agenda del Hotel"
        style={{ border: "1px solid rgb(235,237,240)", backgroundColor: "white", marginBottom: 10 }}
        extra={
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <ButtonItem blanco size="small" icon={<ReloadOutlined />} disabled={!proyectoId} onClick={consultar}>
              Actualizar
            </ButtonItem>
            <ButtonItem
              size="small"
              className="botonAgregar"
              icon={<PlusOutlined />}
              disabled={!proyectoId || !habitaciones.length}
              onClick={() => setModal({ visible: true, tarea: null })}
            >
              Nueva tarea
            </ButtonItem>
          </div>
        }
      />

      <div style={{ background: "#fff", borderRadius: 14, padding: 16 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Hotel</div>
            <SelectItem width={220} placeholder="Hotel" value={proyectoId} onChange={setProyectoId}>
              {todosProyectos.map((valorProyecto) => (
                <Option key={valorProyecto._id} value={valorProyecto._id}>
                  {valorProyecto.nombre}
                </Option>
              ))}
            </SelectItem>
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Piso</div>
            <SelectItem
              width={180}
              placeholder="Todos los pisos"
              value={nivelHotelId}
              disabled={!proyectoId}
              allowClear
              onChange={setNivelHotelId}
            >
              {niveles.map((valorNivel) => (
                <Option key={valorNivel._id} value={valorNivel._id}>
                  {valorNivel.nombre}
                </Option>
              ))}
            </SelectItem>
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Responsable</div>
            <SelectEmpleadosItem
              empresaId={empresaId}
              headersToken={headersToken}
              value={responsableEmpleadoId}
              onChange={setResponsableEmpleadoId}
              width={220}
              allowClear
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Estatus</div>
            <SelectItem
              width={230}
              mode="multiple"
              placeholder="Todos"
              value={estatus}
              onChange={setEstatus}
            >
              {Object.keys(ESTATUS_TAREA).map((clave) => (
                <Option key={clave} value={clave}>
                  {ESTATUS_TAREA[clave].etiqueta}
                </Option>
              ))}
            </SelectItem>
          </div>
        </div>

        <Spin spinning={cargando}>
          {!proyectoId ? (
            <Empty description="Elige un hotel para ver su agenda" style={{ padding: 60 }} />
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 330px", gap: 18, alignItems: "start" }}>
              <div>
                <CalendarItem
                  events={eventos}
                  loading={cargando}
                  onRangeChange={manejarRango}
                  onActualizado={consultar}
                  onEditarEvento={(evento) => {
                    const tarea = tareas.find((valor) => String(valor._id) === String(evento.id));
                    if (tarea) setModal({ visible: true, tarea });
                  }}
                  // Clic en un día del calendario cambia la lista de la derecha. Es una comodidad:
                  // el selector de fecha de la lista hace lo mismo y no depende de que
                  // react-big-calendar dispare el evento.
                  selectable
                  onSelectSlot={(slot) => slot?.start && setDiaElegido(moment(slot.start))}
                  // El drawer del calendario es compartido con el CRM y sus botones de completar /
                  // comentar / eliminar pegan a los endpoints de allá. Aquí solo muestra el detalle;
                  // las acciones están en la lista del día, con el contrato de este módulo.
                  soloLectura
                />
              </div>

              {/* La lista del día: es lo que se imprime o se dicta en la junta de la mañana. */}
              <div style={{ border: "1px solid #eef0f6", borderRadius: 10, padding: 14 }}>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>Día</div>
                  <DatePickerItem
                    width="100%"
                    value={diaElegido}
                    allowClear={false}
                    onChange={(valor) => setDiaElegido(valor || moment())}
                  />
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                    {tareasDelDia.length} tarea(s) ese día
                  </div>
                </div>

                {!tareasDelDia.length ? (
                  <div style={{ color: "#6b7280", fontSize: 13 }}>
                    Nada agendado ese día. Elige otro en el calendario.
                  </div>
                ) : (
                  tareasDelDia.map((tarea) => (
                    <div
                      key={tarea._id}
                      style={{
                        border: "1px solid #eef0f6",
                        borderLeft: `3px solid ${colorDeTarea(tarea)}`,
                        borderRadius: 8,
                        padding: 10,
                        marginBottom: 8,
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>{tarea.titulo}</div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                        Hab. {tarea.habitacionNumero}
                        {tarea.nivelNombre ? ` · ${tarea.nivelNombre}` : ""}
                        {tarea.todoElDia === false ? ` · ${moment(tarea.fechaInicio).format("HH:mm")}` : ""}
                      </div>
                      <div style={{ fontSize: 12, marginTop: 2 }}>
                        {tarea.responsableNombre || (
                          <span style={{ color: "#b45309" }}>Sin responsable</span>
                        )}
                      </div>

                      <div style={{ marginTop: 6, display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
                        <Tag color={ESTATUS_TAREA[tarea.estatus]?.color}>
                          {ESTATUS_TAREA[tarea.estatus]?.etiqueta}
                        </Tag>
                        <Tag>{prioridadDe(tarea.prioridad).etiqueta}</Tag>

                        {tarea.estatus === "pendiente" ? (
                          <>
                            <ButtonItem
                              size="small"
                              verde
                              icon={<CheckOutlined />}
                              onClick={() => cambiarEstatus(tarea, "terminada")}
                            >
                              Terminar
                            </ButtonItem>
                            <ButtonItem
                              size="small"
                              blanco
                              icon={<StopOutlined />}
                              onClick={() => cambiarEstatus(tarea, "cancelada")}
                            >
                              Cancelar
                            </ButtonItem>
                          </>
                        ) : (
                          <ButtonItem
                            size="small"
                            blanco
                            icon={<UndoOutlined />}
                            onClick={() => cambiarEstatus(tarea, "pendiente")}
                          >
                            Reabrir
                          </ButtonItem>
                        )}

                        <ButtonItem
                          size="small"
                          blanco
                          icon={<EditOutlined />}
                          onClick={() => setModal({ visible: true, tarea })}
                        >
                          Editar
                        </ButtonItem>

                        <Popconfirm
                          title="¿Eliminar esta tarea? Si ya no aplica, cancélala en vez de borrarla."
                          okText="Eliminar"
                          cancelText="No"
                          onConfirm={() => eliminar(tarea)}
                        >
                          <ButtonItem size="small" rojo2>
                            Eliminar
                          </ButtonItem>
                        </Popconfirm>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </Spin>
      </div>

      <ModalTareaHabitacion
        visible={modal.visible}
        tarea={modal.tarea}
        habitaciones={habitaciones}
        fechaSugerida={diaElegido}
        onCancel={cerrarModal}
        onGuardado={guardado}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(AgendaHotel);
